import { redactShopifyInstallTelemetry } from '@/lib/shopifyInstallTelemetry';
import {
    RefreshWorkspaceOptions,
    SwitchWorkspaceOptions,
    useWorkspaces,
    Workspace
} from '@/contexts/AuthContext/useWorkspaces';
import { useDialog } from '@/contexts/DialogContext';
import {
    AUTH_STORAGE_KEY,
    authService, supabase, VerifyAuthResponse
} from '@common/api/authService';
import { User } from '@supabase/supabase-js';
// 重新导出工具函数
export { getMemberAvatarUrl, getUserAvatarUrl } from './utils';
export { useOauth } from './useOauth';
//@ts-ignore
import Cookies from 'js-cookie';
import React, {
    createContext,
    useCallback,
    useContext, useEffect, useMemo, useRef,
    useState
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// import { NangoIntegrations, useNango } from './useNango'; // Deprecated - 已整合到 useWorkspaceIntegrations
import { useUserStorage } from '@common/hooks/useUserStorage';
import { useTranslation } from '@client-app/i18n';
import {
    GOOGLE_ANALYTICS_ADMIN_MEASUREMENT_ID,
    syncGoogleAnalyticsUserContext
} from '@common/utils/googleAnalytics';
import { buildOAuthSuccessLoginRedirectUrl } from '@/utils/oauthSuccessRedirect';
import { hasPendingPasswordReset } from '@/utils/pendingPasswordReset';
import GlobalLoading from '../../components/GlobalLoading';
import { usePermissions, UsePermissionsReturn } from './usePermissions';
import {
    applyBillingPrefetchProfileState,
    applyWorkspaceSubscriptionToProfile,
    buildWorkspaceSubscriptionFromSetupCardStatus,
    shouldApplyBillingProfileRefresh
} from './refreshProfileFromServer';
import {
    applyPaymentReturnPlanToProfile,
    readPaymentReturnPlanOverride,
    shouldApplyPaymentReturnPlanOverride,
} from './paymentReturnPlanSync';
import { readPaymentReturnWorkspaceId } from '@/utils/paymentReturnWorkspace';
import { billingService } from '@common/api/billingService';
import { SKIP_WORKSPACE_ID_HEADER } from '@common/api/api';
import { getCurrentProductCode, getProductScopedStorageKey, isPluginProduct } from '@common/api/productContext';
import { getAdminProductFeatures } from '@/utils/adminProductFeatures';
import { SyncStatusState, useSyncStatus } from './useSyncStatus';
import { UserBilling, useUserBilling } from './useUserBilling';
import { useWorkspaceIntegrations, UseWorkspaceIntegrationsReturn } from './useWorkspaceIntegrations';
import { captureSentryError, logSentryEvent, setSentryUserContext } from '@/lib/sentry';
import {
    getRouteBehavior,
    shouldLoadWorkspaceIntegrationsForRoute,
} from '@/routeBehavior';
import type {
    MemberStatus,
    UseWorkspaceMembersReturn,
    WorkspaceInvitation,
    WorkspaceMemberDisplay
} from './useWorkspaceMembers';
import { useWorkspaceMembers } from './useWorkspaceMembers';
import type { WorkspaceRole } from './useWorkspaces';
import { useOauth } from './useOauth';

// 重新导出常用类型
export type {
    MemberStatus, WorkspaceInvitation, WorkspaceMemberDisplay, WorkspaceRole
};

// 重新导出权限相关类型和枚举
    export type {
        RolePermission,
        UsePermissionsReturn
    } from './usePermissions';

export { Permission } from './permissions';


declare global {
    interface Window {
        isShopify: boolean;
    }
}

interface AuthContextType {
    openSignInModal: () => void;
    logout: () => void,
    user: User | null;
    profile: VerifyAuthResponse | null;
    setProfile: (profile: VerifyAuthResponse | null) => void;
    userBilling: UserBilling;
    integrations: UseWorkspaceIntegrationsReturn;
    workspaceMembers: UseWorkspaceMembersReturn;
    syncStatus: SyncStatusState;
    permissions: UsePermissionsReturn;
    isLoadingProfile: boolean;
    workspaces: Workspace[];
    currentWorkspace: Workspace | null;
    isLoadingWorkspaces: boolean;
    workspacesError: Error | null;
    prefetchedBillingWorkspaceId: string | null;
    billingPrefetchingWorkspaceId: string | null;
    refreshWorkspaces: (options?: RefreshWorkspaceOptions) => Promise<void>;
    switchWorkspace: (workspaceId: string, options?: SwitchWorkspaceOptions | Record<string, string>) => void;
    setCurrentWorkspace: (workspaceId: string | null) => void;
    applyWorkspaceSubscriptionPlan: (
        workspaceId: string,
        planCode?: string | null,
        planName?: string | null,
        billingCycle?: string | null,
    ) => void;
    isGlobalLoading: boolean;
    showGlobalLoading: () => void;
    hideGlobalLoading: () => void;
}

// 创建一个默认值
const defaultContextValue: AuthContextType = {
    openSignInModal: () => { },
    logout: () => { },
    user: null,
    profile: null,
    setProfile: () => {},
    isLoadingProfile: false,
    workspaces: [],
    currentWorkspace: null,
    isLoadingWorkspaces: false,
    workspacesError: null,
    prefetchedBillingWorkspaceId: null,
    billingPrefetchingWorkspaceId: null,
    refreshWorkspaces: async (_options?: RefreshWorkspaceOptions) => {},
    switchWorkspace: () => {},
    setCurrentWorkspace: () => {},
    applyWorkspaceSubscriptionPlan: () => {},
    isGlobalLoading: false,
    showGlobalLoading: () => {},
    hideGlobalLoading: () => {},
    userBilling: {
        userPlan: null,
        summary: null,
        userPoints: null,
        pointsHistory: {
            pagination: {
                total: 0,
                size: 0,
                totalPages: 0,
                hasPrevious: false,
                hasNext: false,
                page: 0
            },
            data: []
        },
        invoices: {
            pagination: {
                total: 0,
                size: 0,
                totalPages: 0,
                hasPrevious: false,
                hasNext: false,
                page: 0
            },
            invoices: []
        },
        invoicesWorkspaceId: null,
        loading: {
            plan: false,
            points: false,
            history: false,
            invoices: false
        },
        error: {
            plan: null,
            points: null,
            history: null,
            invoices: null
        },
        applyWorkspacePlan: () => {},
        fetchSummary: async () => Promise.resolve(),
        fetchUserPoints: async () => Promise.resolve(),
        fetchPointsHistory: async () => Promise.resolve(),
        fetchInvoices: async (_page?: number, _size?: number, _workspaceId?: string | null) => Promise.resolve(),
        refreshAll: async () => Promise.resolve(),
        planText: {
            planDisplayText: '',
            badge: '',
            expirationText: '',
            buttonText: '',
            status: 'none'
        },

        isExpired: false,
        isCanceled: false,
        isTrial: false,
    },
    integrations: {} as any,
    workspaceMembers: {
        members: [],
        invitations: [],
        pendingInvites: [],
        pendingOwnerTransfer: null,
        status: 'idle',
        isLoading: false,
        isLoadingInvitations: false,
        error: null,
        refresh: async () => {},
        refreshInvitations: async () => {},
        refreshPendingOwnerTransfer: async () => {},
        inviteMember: async () => {},
        removeMember: async () => {},
        updateMemberRole: async () => {},
        transferOwnership: async () => {},
        cancelOwnerTransfer: async () => {},
        currentMembershipRole: null,
        currentWorkspaceRole: null,
    },
    syncStatus: {
        syncStatuses: [],
        loading: false,
        syncCompleted: false
    },
    permissions: {
        status: 'idle',
        isLoading: false,
        error: null,
        role: null,
        roleKey: null,
        roleName: null,
        isOwner: false,
        isAdmin: false,
        isAdminRole: false,
        canTransferOwnership: false,
        checkPermissions: () => false,
        refresh: async () => {},
    },
    // shopifyEmbed: {
    //     isEmbedded: false,
    //     isAppBridgeReady: false,
    //     shopify: null,
    //     shopDomain: null,
    //     redirectToShopify: () => {}
    // }
};

// 使用默认值创建 Context
export const AuthContext = createContext<AuthContextType>(defaultContextValue);

const shouldRetryVerifyAuthWithoutWorkspace = (error: unknown) => {
    const axiosError = error as {
        code?: string;
        response?: {
            status?: number;
            code?: string;
            data?: { error?: string; message?: string };
            message?: string;
        };
        message?: string;
    };
    const status = axiosError?.response?.status;
    const code = axiosError?.code || axiosError?.response?.code;
    const message = axiosError?.response?.data?.error
        || axiosError?.response?.data?.message
        || axiosError?.response?.message
        || axiosError?.message;
    const isWorkspaceContextError =
        message === 'Workspace not found'
        || message === 'Invalid workspace ID'
        || message === 'You do not have permission to access this workspace';

    return isWorkspaceContextError && (status === 400 || status === 403 || code === '400' || code === '1003');
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoadingProfile, setIsLoadingProfile] = useState(true);
    const [profile, setProfile] = useState<VerifyAuthResponse | null>(null);
    const [isGlobalLoading, setIsGlobalLoading] = useState(true);
    const [prefetchedBillingWorkspaceId, setPrefetchedBillingWorkspaceId] = useState<string | null>(null);
    const [billingPrefetchingWorkspaceId, setBillingPrefetchingWorkspaceId] = useState<string | null>(null);
    const prefetchedBillingWorkspaceRef = useRef<string | null>(null);
    const billingPrefetchingWorkspaceRef = useRef<string | null>(null);
    const billingPrefetchPromiseRef = useRef<{ workspaceId: string; promise: Promise<void> } | null>(null);
    const profileRef = useRef<VerifyAuthResponse | null>(null);
    const currentWorkspaceIdRef = useRef<string | null>(null);
    const invoicesWorkspaceIdRef = useRef<string | null>(null);
    const userBilling = useUserBilling(profile);
    profileRef.current = profile;
    invoicesWorkspaceIdRef.current = userBilling.invoicesWorkspaceId;
    const {set} = useUserStorage();
    const { getOAuthContext } = useOauth();
    const { t } = useTranslation();

    const navigate = useNavigate();
    const location = useLocation();
    const currentRouteBehavior = useMemo(
        () => getRouteBehavior(location.pathname),
        [location.pathname],
    );
    const shouldSuppressWorkspaceEffects =
        currentRouteBehavior.pageKind === 'onboarding'
        || currentRouteBehavior.pageKind === 'payment-return'
        || currentRouteBehavior.pageKind === 'owner-transfer-accept';
    const shouldSuppressAuthGlobalLoading = currentRouteBehavior.suppressAuthGlobalLoading;
    const shouldPauseWorkspaceLoading =
        isLoadingProfile && currentRouteBehavior.pageKind !== 'onboarding';

    const showGlobalLoading = useCallback(() => setIsGlobalLoading(true), []);
    const hideGlobalLoading = useCallback(() => setIsGlobalLoading(false), []);

    const openSignInModal = () => {
        navigate(`/auth?redirect=${window.location.href}`);
    };

    const { showDialog } = useDialog();

    const clearCachedWorkspaceContext = () => {
        const currentWorkspaceStorageKey = getProductScopedStorageKey(
            'currentWorkspaceId',
            getCurrentProductCode(),
        );
        set(currentWorkspaceStorageKey, '');
        window.Bustly = {
            ...(window.Bustly || {}),
            workspace_id: undefined,
        };
    };

    const _logout = async () => {
        Cookies.remove(AUTH_STORAGE_KEY);
        try {
            const result = await supabase.auth.signOut({ scope: 'local' });
            if(result.error){
                throw result.error;
            }else{
                localStorage.removeItem(AUTH_STORAGE_KEY);
                window.location.href = '/'
            }
        } catch (error) {
            console.error('Logout failed:', error);
            localStorage.removeItem(AUTH_STORAGE_KEY);
            window.location.href = '/';
        }
    };

    const logout = async () => {
        const accountName = user?.email
            || t('website.logoutDialog.accountFallback');

        // Show a confirmation dialog
        const confirmed = await showDialog({
            title: t('website.logoutDialog.title'),
            subtitle: (
                <span className="block [overflow-wrap:anywhere]">
                    {t('website.logoutDialog.signedInAs', { account: accountName })}
                </span>
            ),
            confirmText: t('website.logoutDialog.confirm'),
            cancelText: t('website.logoutDialog.cancel'),
        });

        if (confirmed) {
            _logout();
        }
    }

    useEffect(() => {
        const init = async () => {
            const initStartedAt = Date.now();
            // 检测 URL 中的错误参数（Supabase 认证错误）
            // 检查 query string 和 hash fragment
            const searchParams = new URLSearchParams(window.location.search);
            const hashParams = new URLSearchParams(window.location.hash.split('#')[1] || '');
            const currentPath = window.location.pathname;
            const routeBehavior = getRouteBehavior(currentPath);
            const isAuthRoute = routeBehavior.pageKind === 'auth';
            const isOAuthSuccessRoute = routeBehavior.pageKind === 'oauth-success';
            const isNativeOAuthCallbackRoute = routeBehavior.pageKind === 'oauth-callback';
            const isAnonymousRoute = routeBehavior.allowAnonymous;

            console.info('[AuthContext] init:start', {
                pathname: currentPath,
                search: redactShopifyInstallTelemetry(window.location.search),
                productCode: getCurrentProductCode(),
                pageKind: routeBehavior.pageKind,
                suppressAuthGlobalLoading: routeBehavior.suppressAuthGlobalLoading,
            });

            const errorCode = searchParams.get('error_code') || hashParams.get('error_code');
            const error = searchParams.get('error') || hashParams.get('error');
            const errorDesc = searchParams.get('error_description') || hashParams.get('error_description');

            // 如果有错误参数且不在错误页面，重定向到错误页面
            if ((errorCode || error || errorDesc) && !window.location.pathname.includes('/auth/error')) {
                logSentryEvent('auth.redirect_error.detected', {
                    area: 'auth',
                    error_code: errorCode,
                    error,
                    has_description: Boolean(errorDesc),
                }, 'warn');
                const errorParams = new URLSearchParams();
                if (errorCode) errorParams.set('error_code', errorCode);
                if (error) errorParams.set('error', error);
                if (errorDesc) errorParams.set('error_description', errorDesc);

                window.location.href = `/admin/auth/error?${errorParams.toString()}`;
                return;
            }

            console.info('[AuthContext] init:getSession:start');
            const { data: { session } } = await supabase.auth.getSession();
            console.info('[AuthContext] init:getSession:done', {
                hasSession: Boolean(session?.user),
                userId: session?.user?.id || null,
                durationMs: Date.now() - initStartedAt,
            });
            if (session?.user) {
                setUser(session.user);
                try {
                    let verifyAuth: VerifyAuthResponse;
                    try {
                        console.info('[AuthContext] init:verifyAuth:start', {
                            userId: session.user.id,
                            workspaceId: window.Bustly?.workspace_id || null,
                            skipWorkspace: false,
                        });
                        verifyAuth = await authService.verifyAuth();
                    } catch (error) {
                        if (!shouldRetryVerifyAuthWithoutWorkspace(error)) {
                            throw error;
                        }
                        console.warn('[AuthContext] init:verifyAuth:retryWithoutWorkspace', {
                            userId: session.user.id,
                            workspaceId: window.Bustly?.workspace_id || null,
                            message: (error as any)?.message,
                            code: (error as any)?.code,
                        });
                        clearCachedWorkspaceContext();
                        verifyAuth = await authService.verifyAuth({
                            headers: { [SKIP_WORKSPACE_ID_HEADER]: 'true' },
                        });
                    }
                    console.info('[AuthContext] init:verifyAuth:success', {
                        userId: session.user.id,
                        workspaceId: verifyAuth.workspace?.id || null,
                        hasWorkspacePlan: Boolean(verifyAuth.workspacePlan),
                        durationMs: Date.now() - initStartedAt,
                    });
                    setProfile(verifyAuth);
                    setSentryUserContext({
                        id: session.user.id,
                        email: session.user.email,
                        username: session.user.user_metadata?.full_name,
                        workspaceId: verifyAuth.workspace?.id,
                        workspaceRole: verifyAuth.workspace?.workspaceRoleKey || verifyAuth.workspace?.role,
                    });
                    logSentryEvent('auth.session.verified', {
                        area: 'auth',
                        user_id: session.user.id,
                        workspace_id: verifyAuth.workspace?.id,
                        has_workspace_plan: Boolean(verifyAuth.workspacePlan),
                    });
                    Cookies.set(AUTH_STORAGE_KEY, session.access_token, {
                        path: '/',        // 全站有效
                        sameSite: 'lax',  // 或 'none' + secure
                        secure: true      // HTTPS 必须
                    })
                    const workspaceId = verifyAuth.workspace?.id || (window as any).Bustly?.workspace_id;
                    if (workspaceId && getOAuthContext()) {
                        console.log('[AuthContext] Detected pending desktop OAuth context; continuation is owned by ClientAuth pages', {
                            workspaceId,
                            currentPath,
                            isAuthRoute,
                            isOAuthSuccessRoute,
                        });
                    } else {
                        console.log('[AuthContext] No pending desktop OAuth continuation detected during auth init');
                    }
                } catch (error: any) {
                    console.error('[AuthContext] init:verifyAuth:failed', {
                        message: error?.message,
                        code: error?.code,
                        status: error?.response?.status,
                        durationMs: Date.now() - initStartedAt,
                    });
                    if(error.message === '401'){
                        logout();
                        openSignInModal();
                    }
                    if(error.code === '1003'){
                        clearCachedWorkspaceContext();
                        window.location.reload();
                    }

                    logSentryEvent('auth.verify.failed', {
                        area: 'auth',
                        message: error?.message,
                    }, 'error');
                    captureSentryError(error, {
                        area: 'auth',
                        flow: 'verify_auth',
                        message: error?.message,
                    });
                    console.error('Verify auth failed:', error.message);
                }finally{
                    setIsLoadingProfile(false);
                    console.info('[AuthContext] init:profileLoading:released', {
                        hasSession: Boolean(session?.user),
                        durationMs: Date.now() - initStartedAt,
                    });
                    const shouldLetAdminLayoutFinishLoading = Boolean(
                        session?.user
                        && routeBehavior.deferGlobalLoadingToLayout
                        && routeBehavior.usesAdminLayout
                        && !isNativeOAuthCallbackRoute
                    );
                    if (!shouldLetAdminLayoutFinishLoading) {
                        hideGlobalLoading();
                    }
                }
            }else{
                // 未登录且不在 /auth 页面，跳转到登录页
                console.info('[AuthContext] init:noSession', {
                    pathname: currentPath,
                    isAuthRoute,
                    isAnonymousRoute,
                    durationMs: Date.now() - initStartedAt,
                });
                if (isOAuthSuccessRoute) {
                    window.location.replace(buildOAuthSuccessLoginRedirectUrl(window.location.search));
                    return;
                } else if (!isAuthRoute && !isAnonymousRoute) {
                    const redirectUrl = window.location.href;
                    navigate(`/auth?redirect=${encodeURIComponent(redirectUrl)}`);
                }
                setIsLoadingProfile(false);
                hideGlobalLoading();
            }
        }

        init();
    }, [])

    // 监听 Supabase 的 auth 状态变化
    useEffect(() => {
        // 监听 auth 状态变化
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
            console.log('Auth state change:', event, session);
            if (event === 'SIGNED_IN' && session?.user) {
                setUser(session.user);
                setSentryUserContext({
                    id: session.user.id,
                    email: session.user.email,
                    username: session.user.user_metadata?.full_name,
                    workspaceId: profileRef.current?.workspace?.id,
                    workspaceRole: profileRef.current?.workspace?.workspaceRoleKey || profileRef.current?.workspace?.role,
                });
            } else if (event === 'SIGNED_OUT') {
                setUser(null);
                setProfile(null);
                setSentryUserContext(null);
            } else if (event === 'USER_UPDATED' && session?.user) {
                // 更新用户信息（如头像、姓名等）
                setUser(session.user);
                setSentryUserContext({
                    id: session.user.id,
                    email: session.user.email,
                    username: session.user.user_metadata?.full_name,
                    workspaceId: profileRef.current?.workspace?.id,
                    workspaceRole: profileRef.current?.workspace?.workspaceRoleKey || profileRef.current?.workspace?.role,
                });
            } else if (event === 'INITIAL_SESSION') {
                // 初始化会话，首次加载时触发
                if (session?.user) {
                    setUser(session.user);
                } else {
                    setUser(null);
                }
            } else if (event === 'PASSWORD_RECOVERY') {
                if (hasPendingPasswordReset()) {
                    logSentryEvent('auth.password_recovery.navigation_suppressed', {
                        area: 'auth',
                        current_path: window.location.pathname,
                    });
                    return;
                }
                // 密码恢复事件，跳转到首页并打开 profile 设置页
                navigate('/?setting_modal=profile');
            }
        });

        // 清理订阅
        return () => subscription.unsubscribe();
    }, []);

    const {
        workspaces, 
        currentWorkspace,
        error: workspacesError,
        isLoading: isLoadingWorkspaces, 
        refreshWorkspaces,
        switchWorkspace,
        setCurrentWorkspace,
        applyWorkspaceSubscriptionPlan,
    } = useWorkspaces(shouldPauseWorkspaceLoading ? undefined : user?.id);
    currentWorkspaceIdRef.current = currentWorkspace?.id || null;

    const workspaceEffectsWorkspaceId = shouldSuppressWorkspaceEffects
        ? undefined
        : currentWorkspace?.id;
    const workspaceEffectsRole = shouldSuppressWorkspaceEffects
        ? null
        : (currentWorkspace?.role || profile?.workspace?.role || null);
    const shouldLoadWorkspaceIntegrations = Boolean(
        workspaceEffectsWorkspaceId
        && !shouldSuppressWorkspaceEffects
        && shouldLoadWorkspaceIntegrationsForRoute(
            location.pathname,
            location.search,
        )
    );

    const integrations = useWorkspaceIntegrations(workspaceEffectsWorkspaceId, {
        enabled: shouldLoadWorkspaceIntegrations,
    });

    const workspaceInvitationEmail = user?.email || profile?.userInfo?.email || undefined;
    const shouldLoadWorkspaceMembers = getAdminProductFeatures().memberInvites;
    const workspaceMembers = useWorkspaceMembers(
        workspaceEffectsWorkspaceId,
        user?.id || profile?.userInfo?.id,
        workspaceInvitationEmail,
        { enabled: shouldLoadWorkspaceMembers },
    );
    const syncStatus = useSyncStatus(workspaceEffectsWorkspaceId);

    // 获取当前用户在当前 workspace 下的角色权限（优先当前 workspace，回退 profile）
    const permissions = usePermissions(
        workspaceEffectsRole as WorkspaceRole | null,
        workspaceEffectsWorkspaceId || null
    );

    useEffect(() => {
        const analyticsUserId = user?.id || profile?.userInfo?.id || null;
        const analyticsWorkspaceId = currentWorkspace?.id || profile?.workspace?.id || null;

        void syncGoogleAnalyticsUserContext(
            analyticsUserId
                ? {
                    userId: analyticsUserId,
                    workspaceId: analyticsWorkspaceId,
                }
                : null,
            GOOGLE_ANALYTICS_ADMIN_MEASUREMENT_ID
        );
    }, [
        currentWorkspace?.id,
        profile?.userInfo?.id,
        profile?.workspace?.id,
        user?.id,
    ]);

    useEffect(() => {
        const currentWorkspaceId = currentWorkspace?.id;
        if (shouldSuppressWorkspaceEffects) return;
        if (!currentWorkspaceId) return;
        if (!profile) return;
        if (!getRouteBehavior(window.location.pathname).usesAdminLayout) return;
        const initialPaymentReturnPlanOverride = isPluginProduct(getCurrentProductCode())
            ? readPaymentReturnPlanOverride(
                new URLSearchParams(location.search),
                readPaymentReturnWorkspaceId(),
            )
            : null;
        const shouldApplyInitialPaymentReturnPlanOverride = shouldApplyPaymentReturnPlanOverride({
            override: initialPaymentReturnPlanOverride,
            workspaceId: currentWorkspaceId,
        });
        if (!shouldApplyInitialPaymentReturnPlanOverride && prefetchedBillingWorkspaceRef.current === currentWorkspaceId) return;
        if (!shouldApplyInitialPaymentReturnPlanOverride && billingPrefetchingWorkspaceRef.current === currentWorkspaceId) return;
        if (!shouldApplyInitialPaymentReturnPlanOverride && billingPrefetchPromiseRef.current?.workspaceId === currentWorkspaceId) {
            setBillingPrefetchingWorkspaceId(currentWorkspaceId);
            return;
        }

        billingPrefetchingWorkspaceRef.current = currentWorkspaceId;
        setBillingPrefetchingWorkspaceId(currentWorkspaceId);

        const clearBillingPrefetchingWorkspace = () => {
            if (billingPrefetchingWorkspaceRef.current === currentWorkspaceId) {
                billingPrefetchingWorkspaceRef.current = null;
            }
            setBillingPrefetchingWorkspaceId(prev => prev === currentWorkspaceId ? null : prev);
        };

        const prefetchBillingInvoices = async () => {
            if (isPluginProduct(getCurrentProductCode())) return;
            if (invoicesWorkspaceIdRef.current !== currentWorkspaceId) {
                await userBilling.fetchInvoices(1, 5, currentWorkspaceId);
            }
        };

        const prefetchBilling = async () => {
            const invoicePrefetchPromise = prefetchBillingInvoices().catch((error) => {
                console.error('[AuthContext] Failed to prefetch billing invoices:', error);
            });
            if (isPluginProduct(getCurrentProductCode())) {
                if (currentWorkspaceIdRef.current === currentWorkspaceId) {
                    setPrefetchedBillingWorkspaceId(currentWorkspaceId);
                    prefetchedBillingWorkspaceRef.current = currentWorkspaceId;
                    clearBillingPrefetchingWorkspace();
                }
                await invoicePrefetchPromise;
                return;
            }
            const getActivePaymentReturnPlanOverride = () => {
                if (!isPluginProduct(getCurrentProductCode())) {
                    return null;
                }
                const activeOverride = readPaymentReturnPlanOverride(
                    new URLSearchParams(window.location.search),
                    readPaymentReturnWorkspaceId(),
                );
                return shouldApplyPaymentReturnPlanOverride({
                    override: activeOverride,
                    workspaceId: currentWorkspaceId,
                })
                    ? activeOverride
                    : null;
            };
            if (isPluginProduct(getCurrentProductCode())) {
                const activePluginPlanOverride = getActivePaymentReturnPlanOverride();
                if (activePluginPlanOverride) {
                    const workspaceProfile = applyPaymentReturnPlanToProfile(
                        profileRef.current,
                        activePluginPlanOverride.planCode,
                        activePluginPlanOverride.planName,
                        activePluginPlanOverride.billingCycle,
                    );
                    if (workspaceProfile && currentWorkspaceIdRef.current === currentWorkspaceId) {
                        userBilling.applyWorkspacePlan(workspaceProfile.workspacePlan);
                        if (shouldApplyBillingProfileRefresh(profileRef.current, workspaceProfile)) {
                            setProfile(workspaceProfile);
                        }
                    }
                } else {
                    const pluginWorkspaceSubscription = currentWorkspace?.id === currentWorkspaceId
                        ? currentWorkspace.subscription
                        : null;
                    const pluginBillingPrefetchProfile = applyWorkspaceSubscriptionToProfile(
                        profileRef.current,
                        pluginWorkspaceSubscription,
                    );
                    if (
                        pluginBillingPrefetchProfile
                        && currentWorkspaceIdRef.current === currentWorkspaceId
                    ) {
                        userBilling.applyWorkspacePlan(pluginBillingPrefetchProfile.workspacePlan);
                        if (shouldApplyBillingProfileRefresh(profileRef.current, pluginBillingPrefetchProfile)) {
                            setProfile(pluginBillingPrefetchProfile);
                        }
                    }
                }
                if (currentWorkspaceIdRef.current === currentWorkspaceId) {
                    setPrefetchedBillingWorkspaceId(currentWorkspaceId);
                    prefetchedBillingWorkspaceRef.current = currentWorkspaceId;
                    clearBillingPrefetchingWorkspace();
                }
                await invoicePrefetchPromise;
                return;
            }
            const workspaceSubscription = currentWorkspace?.id === currentWorkspaceId
                ? currentWorkspace.subscription
                : null;
            const workspaceProfileFromSubscription = applyWorkspaceSubscriptionToProfile(
                profileRef.current,
                workspaceSubscription,
            );
            const workspaceProfile = workspaceProfileFromSubscription;

            if (
                workspaceProfile
                && currentWorkspaceIdRef.current === currentWorkspaceId
                && shouldApplyBillingProfileRefresh(profileRef.current, workspaceProfile)
            ) {
                userBilling.applyWorkspacePlan(workspaceProfile.workspacePlan);
                setProfile(workspaceProfile);
            }

            try {
                const setupCardStatus = await billingService.getSetupCardStatus({
                    headers: { 'X-Workspace-Id': currentWorkspaceId },
                });
                let billingWorkspaceSubscription = workspaceSubscription;
                if (!billingWorkspaceSubscription && setupCardStatus?.subscription?.planId) {
                        const plans = await billingService.getPlans({
                            headers: { 'X-Workspace-Id': currentWorkspaceId },
                        }).catch((error) => {
                            console.error('[AuthContext] Failed to hydrate billing plan catalog:', error);
                            return [];
                        });
                    billingWorkspaceSubscription = buildWorkspaceSubscriptionFromSetupCardStatus(
                        setupCardStatus,
                        plans,
                    );
                }
                const billingPrefetchProfile = applyBillingPrefetchProfileState(
                    profileRef.current,
                    setupCardStatus,
                    billingWorkspaceSubscription,
                );
                const verifyAuth = billingPrefetchProfile;
                if (!verifyAuth) return;

                if (currentWorkspaceIdRef.current === currentWorkspaceId) {
                    userBilling.applyWorkspacePlan(verifyAuth.workspacePlan);
                    if (shouldApplyBillingProfileRefresh(profileRef.current, verifyAuth)) {
                        setProfile(verifyAuth);
                    }
                    setPrefetchedBillingWorkspaceId(currentWorkspaceId);
                    prefetchedBillingWorkspaceRef.current = currentWorkspaceId;
                    clearBillingPrefetchingWorkspace();
                }

                if (currentWorkspaceIdRef.current === currentWorkspaceId) {
                    await invoicePrefetchPromise;
                }
            } catch (error) {
                console.error('[AuthContext] Failed to prefetch billing state:', error);
                if (currentWorkspaceIdRef.current === currentWorkspaceId) {
                    prefetchedBillingWorkspaceRef.current = null;
                    clearBillingPrefetchingWorkspace();
                }
                await invoicePrefetchPromise;
            }
        };

        const promise = prefetchBilling().finally(() => {
            if (billingPrefetchPromiseRef.current?.workspaceId === currentWorkspaceId) {
                billingPrefetchPromiseRef.current = null;
            }
            if (currentWorkspaceIdRef.current === currentWorkspaceId) {
                clearBillingPrefetchingWorkspace();
            }
        });
        billingPrefetchPromiseRef.current = { workspaceId: currentWorkspaceId, promise };
    }, [
        currentWorkspace?.id,
        shouldSuppressWorkspaceEffects,
        profile,
        setProfile,
        userBilling.applyWorkspacePlan,
        userBilling.fetchInvoices,
        location.search
    ]);

    const value = {
        openSignInModal,
        logout,
        user,
        profile,
        setProfile,
        userBilling,
        integrations,
        workspaceMembers,
        syncStatus,
        permissions,
        isLoadingProfile,
        workspaces,
        currentWorkspace,
        isLoadingWorkspaces,
        workspacesError,
        prefetchedBillingWorkspaceId,
        billingPrefetchingWorkspaceId,
        refreshWorkspaces,
        switchWorkspace,
        setCurrentWorkspace,
        applyWorkspaceSubscriptionPlan,
        isGlobalLoading,
        showGlobalLoading,
        hideGlobalLoading
    };
    return (
        <AuthContext.Provider value={value}>
            <GlobalLoading isOpen={isGlobalLoading && !shouldSuppressAuthGlobalLoading} />
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
